import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Tooltip } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';

/**
 * Tab configuration - defines properties for each tab
 * To add a new tab: Add entry here with label, route, and icons
 */
const TAB_CONFIG = {
  HUB: {
    label: 'WORKSPACE',
    route: '/agent-hub',
    activeIcon: '/resources/icons/hub_active_icon.png',
    inactiveIcon: '/resources/icons/hub_inactive_icon.png'
  },
  RCM: {
    label: 'WORKSPACE',
    route: '/rcm',
    activeIcon: '/resources/icons/rcm_active_icon.png',
    inactiveIcon: '/resources/icons/rcm_inactive_icon.png'
  },
  COLAB: {
    label: 'STUDIO',
    route: '/agent-colab',
    activeIcon: '/resources/icons/colab_active_icon.png',
    inactiveIcon: '/resources/icons/colab_inactive_icon.png'
  },
  MCP: {
    label: 'MCP LAUNCHPAD',
    route: '/mcp',
    activeIcon: '/resources/icons/mcp_active_icon.png',
    inactiveIcon: '/resources/icons/mcp_inactive_icon.png'
  },
  COUNSEL: {
    label: 'AGENT COUNSEL',
    route: '/counsel',
    activeIcon: '/resources/icons/counsel_active_icon.png',
    inactiveIcon: '/resources/icons/counsel_inactive_icon.png'
  },
  PROJECT: {
    label: 'AGENT PROJECTS',
    route: '/projects',
    activeIcon: '/resources/icons/counsel_active_icon.png',
    inactiveIcon: '/resources/icons/counsel_inactive_icon.png'
  },
  OBSERVATORY: {
    label: 'GOVERNANCE AND CONTROL',
    route: '/observe',
    activeIcon: '/resources/icons/obs_active_icon.png',
    inactiveIcon: '/resources/icons/obs_inactive_icon.png'
  },
  TRACE: {
    label: 'LLM TRACING',
    route: '/trace',
    activeIcon: '/resources/icons/obs_active_icon.png',
    inactiveIcon: '/resources/icons/obs_inactive_icon.png'
  },
  FARM: {
    label: 'BACKGROUND JOBS',
    route: '/farm',
    activeIcon: '/resources/icons/farm_active_icon.png',
    inactiveIcon: '/resources/icons/farm_inactive_icon.png'
  },
  WORKBOOK: {
    label: 'AGENT WORKBOOK',
    route: '/workbook',
    activeIcon: '/resources/icons/workbook_active_icon.png',
    inactiveIcon: '/resources/icons/workbook_inactive_icon.png'
  },
  HCC: {
    label: 'WORKSPACE',
    route: '/hcc-home',
    activeIcon: '/resources/icons/workbook_active_icon.png',
    inactiveIcon: '/resources/icons/workbook_inactive_icon.png'
  },
  HCC_CONFIG: {
    label: 'HCC STUDIO',
    route: '/hcc-config',
    activeIcon: '/resources/icons/workbook_active_icon.png',
    inactiveIcon: '/resources/icons/workbook_inactive_icon.png'
  },
  HCC_CODER_REQUESTED_REVIEW: {
    label: 'CODER REQUESTED REVIEW',
    route: '/hcc-review',
    activeIcon: '/resources/icons/workbook_active_icon.png',
    inactiveIcon: '/resources/icons/workbook_inactive_icon.png'
  },
  MANAGE_CHARTS: {
    label: 'MANAGE CHARTS',
    route: '/manage-charts',
    activeIcon: '/resources/icons/workbook_active_icon.png',
    inactiveIcon: '/resources/icons/workbook_inactive_icon.png'
  },
  RAI_BUILDER: {
    label: 'BUILD AI THESIS',
    route: '/rai-builder-home',
    activeIcon: '/resources/icons/workbook_active_icon.png',
    inactiveIcon: '/resources/icons/workbook_inactive_icon.png'
  },
  RAI_DASHBOARD: {
    label: 'RETURN ON AI',
    route: '/rai-dashboard-home',
    activeIcon: '/resources/icons/workbook_active_icon.png',
    inactiveIcon: '/resources/icons/workbook_inactive_icon.png'
  }
};

// Default tab order (fallback if not provided via props)
const DEFAULT_TAB_ORDER = ['HUB', 'RCM', 'PROJECT', 'COLAB', 'FARM', 'WORKBOOK', 'MCP', 'COUNSEL', 'OBSERVATORY', 'HCC', "HCC_CONFIG", "RAI_BUILDER", "RAI_DASHBOARD", "HCC_CODER_REQUESTED_REVIEW", "MANAGE_CHARTS", "TRACE"];

/**
 * TabNavigation - Reusable navigation component for all Home pages
 * 
 * @param {Object} props
 * @param {Array} props.features - Array of enabled feature names (e.g., ['HUB', 'RCM', 'COLAB'])
 * @param {string} props.activeTab - Currently active tab name (e.g., 'HUB')
 * @param {Array} props.tabOrder - Array defining tab display order (from config)
 * @param {function} props.onRefresh - Optional refresh handler for active tab
 * @param {boolean} props.refreshing - Whether refresh is in progress
 */
const TabNavigation = ({ 
  features = [], 
  activeTab, 
  tabOrder = DEFAULT_TAB_ORDER,
  onRefresh, 
  refreshing = false 
}) => {
  const navigate = useNavigate();

  // Use provided tabOrder, fall back to default if empty
  const effectiveTabOrder = tabOrder && tabOrder.length > 0 ?
    tabOrder : DEFAULT_TAB_ORDER;

  // Render a single tab
  const renderTab = (tabKey) => {
    // Skip if feature not enabled for user
    if (!features.includes(tabKey)) return null;
    
    // Skip if tab config doesn't exist
    const config = TAB_CONFIG[tabKey];
    if (!config) {
      console.warn(`Tab config not found for: ${tabKey}`);
      return null;
    }

    const isActive = activeTab === tabKey;

    if (isActive) {
      // Active tab styling - NO ICON - CENTERED TEXT
      return (
        <div 
          key={tabKey}
          style={{
            padding: '0px 16px',  // Symmetric padding
            backgroundColor: '#f8f9fa',
            cursor: 'pointer',
            borderTopLeftRadius: '4px',
            borderTopRightRadius: '4px',
            minWidth: '220px',
            position: 'relative',
            display: 'flex',
            height: '60px',
            alignItems: 'center',
            justifyContent: 'center',  // Center align content
            gap: '6px',
            marginTop: '-10px',
            marginBottom: '-10px',
            marginLeft: '4px'
          }}
        >
          {/* ICON REMOVED */}
          <span style={{ 
            fontSize: '18px', 
            fontWeight: 'bold',
            color: '#333'
          }}>
            {config.label}
          </span>
          
          {/* Refresh button for active tab */}
          {onRefresh && (
            <Tooltip title="Refresh">
              <Button
                type="text"
                shape="circle"
                icon={<ReloadOutlined style={{ fontSize: '12px', color: '#666' }} />}
                onClick={(e) => {
                  e.stopPropagation();
                  onRefresh();
                }}
                loading={refreshing}
                style={{
                  color: '#666',
                  border: '1px solid #ddd',
                  minWidth: '18px',
                  height: '18px'
                }}
              />
            </Tooltip>
          )}
        </div>
      );
    } else {
      // Inactive tab styling - NO ICON - CENTERED TEXT
      return (
        <div 
          key={tabKey}
          style={{
            padding: '0px 24px',  // Symmetric padding
            backgroundColor: 'rgba(255, 255, 255, 0.3)',
            cursor: 'pointer',
            borderTopLeftRadius: '4px',
            borderTopRightRadius: '4px',
            minWidth: '220px',
            display: 'flex',
            height: '60px',
            alignItems: 'center',
            justifyContent: 'center',  // Center align content
            gap: '6px',
            marginTop: '-10px',
            marginBottom: '-10px',
            marginLeft: '4px',
            transition: 'all 0.3s ease'
          }}
          onClick={() => navigate(config.route)}
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.4)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
          }}
        >
          {/* ICON REMOVED */}
          <span style={{ 
            fontSize: '18px', 
            fontWeight: 'bold',
            color: 'rgba(255, 255, 255, 0.9)'
          }}>
            {config.label}
          </span>
        </div>
      );
    }
  };

  return (
    <>
      {/* Onaxis Brand with Logo */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginRight: '20px',
        marginBottom: '-10px'
      }}>
        {/* Logo */}
        <img 
          src="/resources/logos/onaxis-logo-dark.png"  // Update this path to your logo file
          alt="Onaxis Logo"
          style={{ 
            height: '32px',  // Adjust size as needed
            width: 'auto'
          }}
          onError={(e) => { 
            e.target.style.display = 'none';  // Hide if logo not found
          }}

        />
        {/* Brand Text */}
        <div style={{
          fontSize: '22px',
          fontWeight: 'bold',
          color: 'white'
        }}>
          Chiron - Risk Adjustment
        </div>
      </div>

      {/* Render tabs in configured order */}
      {effectiveTabOrder.map(tabKey => renderTab(tabKey))}
    </>
  );
};

export default TabNavigation;

/**
 * USAGE EXAMPLES:
 * 
 * 1. In AgentSpaceHome.jsx (HUB active):
 *    <TabNavigation 
 *      features={features}
 *      activeTab="HUB"
 *      tabOrder={tabOrder}
 *      onRefresh={handleRefreshConfig}
 *      refreshing={false}
 *    />
 * 
 * 2. In AgentRCMHome.jsx (RCM active):
 *    <TabNavigation 
 *      features={features}
 *      activeTab="RCM"
 *      tabOrder={tabOrder}
 *      onRefresh={handleRefreshConfig}
 *      refreshing={false}
 *    />
 * 
 * 3. In AgentColabHome.jsx (COLAB active):
 *    <TabNavigation 
 *      features={features}
 *      activeTab="COLAB"
 *      tabOrder={tabOrder}
 *      onRefresh={handleRefreshDesigns}
 *      refreshing={refreshing}
 *    />
 * 
 * TO ADD A NEW TAB:
 * 1. Add entry to TAB_CONFIG object above
 * 2. Update unified_role_config.json with new tab in TAB_ORDER array
 * 3. Add route in AppRouter.jsx
 * 4. Create the Home component for the new tab
 */